import { createSvgEl } from '@learnforge/demo-render';
import { Fraction } from '@learnforge/demo-math/numeric';

export type RowOp =
  | { kind: 'swap'; a: number; b: number }
  | { kind: 'scale'; row: number; factor: Fraction }
  | { kind: 'add'; sourceRow: number; targetRow: number; factor: Fraction };

export interface AugmentedMatrixOptions {
  matrix: readonly (readonly Fraction[])[];
  augmentColumn: number; // index where the augmented bar is drawn
  width: number;
  height: number;
}

export interface AugmentedMatrixHandle {
  group: SVGGElement;
  applyRowOp(op: RowOp): Promise<void>; // animates; resolves on completion
  destroy(): void;
}

export function createAugmentedMatrix(
  parent: SVGSVGElement | SVGGElement,
  opts: AugmentedMatrixOptions,
): AugmentedMatrixHandle {
  const group = createSvgEl('g');
  parent.appendChild(group);

  // Create a mutable copy of the matrix
  const matrix: Fraction[][] = Array.from({ length: opts.matrix.length }, (_, r) =>
    Array.from({ length: opts.matrix[r]!.length }, (_, c) => opts.matrix[r]![c]!),
  );

  const rows = matrix.length;
  const cols = matrix[0]!.length;
  const cellWidth = opts.width / cols;
  const cellHeight = opts.height / rows;

  const render = (): void => {
    // Clear existing cells and bars
    Array.from(group.children).forEach((child) => {
      if (
        child.getAttribute('data-role') === 'cell' ||
        child.getAttribute('data-role') === 'augment-bar'
      ) {
        group.removeChild(child);
      }
    });

    // Render cells
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        const x = c * cellWidth + cellWidth / 2;
        const y = r * cellHeight + cellHeight / 2;
        const text = createSvgEl('text', {
          'data-role': 'cell',
          'data-row': r.toString(),
          'data-col': c.toString(),
          x: x.toString(),
          y: y.toString(),
          'text-anchor': 'middle',
          'dominant-baseline': 'middle',
          class: 'alg-matrix-cell',
        });
        text.textContent = matrix[r]![c]!.toString();
        group.appendChild(text);
      }
    }

    // Render augment bar (vertical line between augmentColumn-1 and augmentColumn)
    const barX = opts.augmentColumn * cellWidth;
    const line = createSvgEl('line', {
      'data-role': 'augment-bar',
      x1: barX.toString(),
      y1: '0',
      x2: barX.toString(),
      y2: opts.height.toString(),
      class: 'alg-matrix-augment',
    });
    group.appendChild(line);
  };

  // Initial render
  render();

  const applyRowOp = async (op: RowOp): Promise<void> => {
    // Mutate the internal matrix
    if (op.kind === 'swap') {
      const temp = matrix[op.a];
      matrix[op.a] = matrix[op.b]!;
      matrix[op.b] = temp!;
    } else if (op.kind === 'scale') {
      for (let c = 0; c < cols; c += 1) {
        matrix[op.row]![c] = matrix[op.row]![c]!.mul(op.factor);
      }
    } else if (op.kind === 'add') {
      for (let c = 0; c < cols; c += 1) {
        const sourceVal = matrix[op.sourceRow]![c]!;
        const targetVal = matrix[op.targetRow]![c]!;
        matrix[op.targetRow]![c] = targetVal.add(sourceVal.mul(op.factor));
      }
    }

    // Re-render
    render();

    // Wait for animation (or timeout fallback in jsdom)
    return new Promise((resolve) => {
      let animationDone = false;

      const handleTransitionEnd = (): void => {
        animationDone = true;
        resolve();
      };

      group.addEventListener('transitionend', handleTransitionEnd, { once: true });

      const timeoutId = setTimeout(() => {
        if (!animationDone) {
          animationDone = true;
          group.removeEventListener('transitionend', handleTransitionEnd);
          resolve();
        }
      }, 800);

      // Clean up on completion (void to suppress floating promise warning)
      void Promise.resolve().then(() => {
        if (animationDone) {
          clearTimeout(timeoutId);
        }
      });
    });
  };

  const destroy = (): void => {
    if (group.parentNode) {
      group.parentNode.removeChild(group);
    }
  };

  return { group, applyRowOp, destroy };
}
